import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userActions } from '../store/user/actions';
import { ApplicationState } from '../store';
import {
  ButtonBase,
  Menu,
  MenuProps,
  MenuItem,
  makeStyles,
  Typography,
  ListItemText,
  ListItem,
  withStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import { stackActions } from '../store/stack/actions';
import { Department } from '../store/user/types';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    height: '100%',
  },
  button: {
    color: '#fff',
    width: '100%',
    height: '100%',
    padding: 0,
  },
  overflow: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

const StyledMenu = withStyles({
  paper: {
    padding: 0,
    width: '100%',
  },
})((props: MenuProps) => (
  <Menu
    elevation={2}
    getContentAnchorEl={null}
    marginThreshold={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    {...props}
  />
));

const StyledMenuItem = withStyles(() => ({
  root: {
    paddingTop: '1rem',
    paddingBottom: '1rem',
  },
}))(MenuItem);

type Organization = {
  name: string;
  id: string;
};

type OrganizationNode = {
  organization: Organization;
  departments: Department[];
};

const DepartmentSelect = (): React.ReactElement => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const ref1 = React.useRef<HTMLDivElement>(null);

  const [selectedNode, selectNode] = useState<OrganizationNode | null>(null);
  const [nodes, setNodes] = useState<Record<string, OrganizationNode>>({});

  const handleMenuToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    selectNode(null);
  };

  const handleSelect = (department: Department) => {
    dispatch(userActions.setSelectedDepartment(department));
    dispatch(stackActions.getMeasurementCardByDepartmentId(department.id));
    handleClose();
  };

  const selectedDepartment = useSelector(
    (state: ApplicationState) => state.user.selectedDepartment,
  );
  const departments = useSelector(
    (state: ApplicationState) => state.user.departments,
  );

  useEffect(() => {
    const organizations: Organization[] = [];

    if (departments) {
      departments.forEach((department) => {
        const alreadyExist = organizations.some(
          (organization) => organization.id === department.organizationId,
        );
        if (!alreadyExist) {
          organizations.push({
            id: department.organizationId,
            name: department.organizationName,
          });
        }
      });
    }

    if (!departments || departments.length === 0 || !organizations) {
      selectNode(null);
      setNodes({});
      return;
    }

    if (!selectedDepartment) {
      dispatch(userActions.setSelectedDepartment(departments[0]));
    }
    // create organization and department maps
    const organizationMap: Record<string, Organization> = {};
    for (const org of organizations) {
      organizationMap[org.id] = org;
    }

    const departmentMap: Record<string, Department> = {};
    for (const dep of departments) {
      departmentMap[dep.id] = dep;
    }

    const tree: Record<string, OrganizationNode> = {};
    // build select tree from department relations
    for (const department of departments) {
      const organization = organizationMap[department.organizationId];
      if (!organization) {
        continue;
      }

      const node = tree[organization.id];
      if (node) {
        node.departments.push(department);
      } else {
        tree[organization.id] = {
          organization: organization,
          departments: [department],
        };
      }
    }
    // TODO: convert to sorted lists
    setNodes(tree);
  }, [departments]);

  return (
    <div ref={ref1} className={classes.root}>
      <ButtonBase
        className={classes.button}
        onClick={handleMenuToggle}
        disabled={Object.keys(nodes).length === 0}
      >
        <ListItem>
          {selectedDepartment ? (
            <ListItemText>
              <Typography variant="h2" className={classes.overflow}>
                {nodes[selectedDepartment.organizationId]?.organization.name}
              </Typography>
              <Typography variant="h4" className={classes.overflow}>
                {selectedDepartment.name}
              </Typography>
            </ListItemText>
          ) : (
            <ListItemText />
          )}

          {anchorEl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </ListItem>
      </ButtonBase>
      <StyledMenu
        container={ref1.current}
        open={!!anchorEl}
        onClose={handleClose}
        anchorEl={anchorEl}
      >
        {selectedNode
          ? [
              <StyledMenuItem
                divider
                key={selectedNode.organization.name + '-back'}
                onClick={() => selectNode(null)}
              >
                <NavigateBeforeIcon fontSize="large" />
                <ListItemText>
                  <Typography variant="h3" className={classes.overflow}>
                    {selectedNode.organization.name}
                  </Typography>
                </ListItemText>
              </StyledMenuItem>,
              selectedNode.departments.map((dep) => (
                <StyledMenuItem key={dep.id} onClick={() => handleSelect(dep)}>
                  <Typography variant="h3" className={classes.overflow}>
                    {dep.name}
                  </Typography>
                </StyledMenuItem>
              )),
            ]
          : Object.keys(nodes).map((key) => {
              const node = nodes[key];
              return (
                <StyledMenuItem
                  key={node.organization.id}
                  onClick={() => selectNode(node)}
                >
                  <ListItemText>
                    <Typography variant="h3" className={classes.overflow}>
                      {node.organization.name}
                    </Typography>
                  </ListItemText>
                  <NavigateNextIcon fontSize="large" />
                </StyledMenuItem>
              );
            })}
      </StyledMenu>
    </div>
  );
};

export { DepartmentSelect };
